import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Typography,
  Grid,
  CircularProgress,
  Box,
} from "@mui/material";
import OfflinePinIcon from "@mui/icons-material/OfflinePin";
import useAxiosPrivate from "../../common/useAxiosPrivate";
import { toast } from "react-toastify";
import { Checklist } from "../CustomComponents/ChecklistInterface";
import MultiSubheaderTableRowComponent from "../CustomComponents/SubheaderTableRowComponent";
import googleDriveAxios from "../../common/googleDriveAxios";

const QualityInspectionChecklist: React.FC<{
  chassisNo: string;
  setModel: React.Dispatch<React.SetStateAction<string>>;
  model: string;
  setCheckedBy: React.Dispatch<React.SetStateAction<string>>;
  isDisabled: boolean;
}> = ({ chassisNo, setModel, model, setCheckedBy, isDisabled }) => {
  const axiosPrivate = useAxiosPrivate();
  const [loading, setLoading] = useState<boolean>(false);
  const [externalChecklist, setExternalChecklist] = useState<Checklist[]>([]);
  const [entryChecklist, setEntryChecklist] = useState<Checklist[]>([]);
  const [bedChecklist, setBedChecklist] = useState<Checklist[]>([]);
  const [kitchenChecklist, setKitchenChecklist] = useState<Checklist[]>([]);
  const [ensuiteChecklist, setEnsuiteChecklist] = useState<Checklist[]>([]);
  const [drawersChecklist, setDrawersChecklist] = useState<Checklist[]>([]);
  const [othersChecklist, setOthersChecklist] = useState<Checklist[]>([]);
  const [checkedBy, setLocalCheckedBy] = useState<string>("");
  const [date, setDate] = useState<string>("");
  const [apiError, setApiError] = useState<boolean>(false);
  const [completeStatus, setCompleteStatus] = useState<string>("Incomplete");
  const hasUnsavedChanges = chassisNo;

  const [activeCheckpointList, setActiveCheckpointList] = useState<any[]>([]);
  const checkCheckpointRecord = React.useCallback(async () => {
    if (chassisNo) {
      try {
        const queryParams = new URLSearchParams({
          chassis_no: "DC" + chassisNo,
          dept_name: "qa-quality-inspection",
        }).toString();

        const response = await googleDriveAxios.post(
          `/checkpoint-image-record?${queryParams}`
        );
        const data = response.data;
        setActiveCheckpointList(data.recorded_checkpoint_list);
        console.log(data.recorded_checkpoint_list);
      } catch (error) {
        console.error("Error checking images:", error);
      }
    }
  }, [chassisNo]);

  useEffect(() => {
    checkCheckpointRecord();
  }, [checkCheckpointRecord]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  useEffect(() => {
    const fetchData = async () => {
      if (!chassisNo || chassisNo.length < 4 || chassisNo.length > 5) {
        setExternalChecklist([]);
        setEntryChecklist([]);
        setBedChecklist([]);
        setKitchenChecklist([]);
        setEnsuiteChecklist([]);
        setDrawersChecklist([]);
        setOthersChecklist([]);
        setApiError(false);
        setModel("");
        setCompleteStatus("Incomplete");
        return;
      }

      setLoading(true);
      setApiError(false);
      setModel("");
      try {
        const response = await axiosPrivate.post(
          `/prepare_qa_quality_inspection/${chassisNo}`
        );
        const data = response.data;

        const externalItems = (data.externalChecklistItems || []).map(
          (item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          })
        );
        const entryItems = (data.entryChecklistItems || []).map(
          (item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          })
        );
        const bedItems = (data.bedChecklistItems || []).map(
          (item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          })
        );
        const kitchenItems = (data.kitchenChecklistItems || []).map(
          (item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          })
        );
        const ensuiteItems = (data.ensuiteChecklistItems || []).map(
          (item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          })
        );
        const drawersItems = (data.drawersChecklistItems || []).map(
          (item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          })
        );
        const othersItems = (data.othersChecklistItems || []).map(
          (item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          })
        );

        setModel(data.model);
        setExternalChecklist(externalItems);
        setEntryChecklist(entryItems);
        setBedChecklist(bedItems);
        setKitchenChecklist(kitchenItems);
        setEnsuiteChecklist(ensuiteItems);
        setDrawersChecklist(drawersItems);
        setOthersChecklist(othersItems);
        setLocalCheckedBy(data.checkedBy || "");
        setCheckedBy(data.checkedBy || "");

        let formattedDate = "";
        if (data.createdOn) {
          const date = new Date(data.createdOn);
          if (date.getFullYear() !== 1900) {
            formattedDate = data.createdOn ? data.createdOn.split("T")[0] : "";
          }
        }
        setDate(formattedDate);

        const allItems = [
          ...externalItems,
          ...entryItems,
          ...bedItems,
          ...kitchenItems,
          ...ensuiteItems,
          ...drawersItems,
          ...othersItems,
        ];
        const isComplete =
          allItems.length > 0 &&
          allItems.every(
            (item) =>
              item.status === "pass" ||
              item.status === "fail" ||
              item.status === "n/a"
          );
        setCompleteStatus(isComplete ? "Complete" : "Incomplete");
        console.log(
          "fetchData - isComplete:",
          isComplete,
          "completeStatus:",
          isComplete ? "Complete" : "Incomplete",
          "loading:",
          false
        );
      } catch (error: any) {
        console.error("Error fetching data:", error);
        if (error.response?.status === 500) {
          setApiError(true);
        } else {
          toast.error("Failed to fetch data.");
        }
      } finally {
        setLoading(false);
        console.log("fetchData - loading reset to:", false);
      }
    };

    fetchData();
  }, [chassisNo, setModel, axiosPrivate, setCheckedBy]);

  const saveData = async () => {
    if (!date.trim()) {
      toast.error("Date is required.");
      return;
    }
    if (!checkedBy.trim()) {
      toast.error("Checked By field is required.");
      return;
    }

    setLoading(true);
    console.log("saveData - loading set to:", true);
    try {
      const allChecklists = [
        ...externalChecklist,
        ...entryChecklist,
        ...bedChecklist,
        ...kitchenChecklist,
        ...ensuiteChecklist,
        ...drawersChecklist,
        ...othersChecklist,
      ];

      const isComplete =
        allChecklists.length > 0 &&
        allChecklists.every(
          (item) =>
            item.status === "pass" ||
            item.status === "fail" ||
            item.status === "n/a"
        );
      const newCompleteStatus = isComplete ? "Complete" : "Incomplete";
      setCompleteStatus(newCompleteStatus);
      console.log(
        "saveData - isComplete:",
        isComplete,
        "completeStatus:",
        newCompleteStatus
      );

      const payload = {
        chassisNo,
        model: model,
        externalChecklistItems: externalChecklist.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        entryChecklistItems: entryChecklist.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        bedChecklistItems: bedChecklist.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        kitchenChecklistItems: kitchenChecklist.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        ensuiteChecklistItems: ensuiteChecklist.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        drawersChecklistItems: drawersChecklist.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        othersChecklistItems: othersChecklist.map((item) => ({
          title: item.title,
          notes: item.notes,
          status: item.status,
        })),
        checkedBy,
        createdOn: date,
        completeStatus: newCompleteStatus,
      };

      await toast.promise(
        axiosPrivate.post("/create_or_update_qa_quality_inspection", payload),
        {
          pending: "Saving data...",
          success: "Data saved successfully!",
          error: "Failed to save data.",
        }
      );

      try {
        const response = await axiosPrivate.post(
          `/prepare_qa_quality_inspection/${chassisNo}`
        );
        const data = response.data;

        setExternalChecklist(
          (data.externalChecklistItems || []).map((item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          }))
        );
        setEntryChecklist(
          (data.entryChecklistItems || []).map((item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          }))
        );
        setBedChecklist(
          (data.bedChecklistItems || []).map((item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          }))
        );
        setKitchenChecklist(
          (data.kitchenChecklistItems || []).map((item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          }))
        );
        setEnsuiteChecklist(
          (data.ensuiteChecklistItems || []).map((item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          }))
        );
        setDrawersChecklist(
          (data.drawersChecklistItems || []).map((item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          }))
        );
        setOthersChecklist(
          (data.othersChecklistItems || []).map((item: Checklist) => ({
            ...item,
            status: item.status
              ? item.status.trim().toLowerCase()
              : "not checked",
          }))
        );
        setLocalCheckedBy(data.checkedBy || "");
        setCheckedBy(data.checkedBy || "");
        setModel(data.model || "");

        let formattedDate = "";
        if (data.createdOn) {
          const date = new Date(data.createdOn);
          if (date.getFullYear() !== 1900) {
            formattedDate = data.createdOn ? data.createdOn.split("T")[0] : "";
          }
        }
        setDate(formattedDate);
      } catch (error) {
        console.error("Error re-fetching data after save:", error);
        toast.error("Failed to sync data with server.");
      }

      if (checkedBy) {
        const allChecklists = [
          ...externalChecklist,
          ...entryChecklist,
          ...bedChecklist,
          ...kitchenChecklist,
          ...ensuiteChecklist,
          ...drawersChecklist,
          ...othersChecklist,
        ];

        let itemNoCounter = 1;
        const newReworkItems = allChecklists
          .filter((item) => item.status === "fail")
          .flatMap((item) => {
            const title = item.title || "No Title Available";
            const notesStr = item.notes || "";

            if (notesStr.includes(";")) {
              const parts = notesStr
                .split(";")
                .map((part) => part.trim())
                .filter((part) => part);
              return parts.map((note) => ({
                itemNo: itemNoCounter++,
                problemDescription: `${title}: ${note}`,
                comment: "",
                whoFoundFault: checkedBy,
                responsible: "",
                whoDidRework: "",
                timeSpentOnRework: { hours: 0, minutes: 0, seconds: 0 },
                completed: false,
              }));
            } else {
              return [
                {
                  itemNo: itemNoCounter++,
                  problemDescription: `${title}: ${notesStr}`,
                  comment: "",
                  whoFoundFault: checkedBy,
                  responsible: "",
                  whoDidRework: "",
                  timeSpentOnRework: { hours: 0, minutes: 0, seconds: 0 },
                  completed: false,
                },
              ];
            }
          });

        if (newReworkItems.length > 0) {
          const reworkPayload = {
            chassisNo,
            model,
            station_name: "qa-quality-inspection",
            reworkItems: newReworkItems,
          };

          await axiosPrivate.post(`rework/save`, reworkPayload, {
            params: { rework_type: "qa-quality-inspection" },
          });
        }
      }
    } catch (error) {
      console.error("Error saving data:", error);
      toast.error("Failed to save data.");
    } finally {
      setLoading(false);
      console.log(
        "saveData - loading reset to:",
        false,
        "completeStatus:",
        completeStatus
      );
    }
  };

  const handleNotesChange = (index: number, value: string, type: string) => {
    if (isDisabled && completeStatus === "Complete") {
      console.log(
        "Notes change blocked: isDisabled=",
        isDisabled,
        "completeStatus=",
        completeStatus
      );
      return;
    }

    const updateChecklist = (
      checklist: Checklist[],
      setChecklist: React.Dispatch<React.SetStateAction<Checklist[]>>
    ) => {
      const updatedData = [...checklist];
      updatedData[index].notes = value;
      setChecklist(updatedData);
    };

    if (type === "external") {
      updateChecklist(externalChecklist, setExternalChecklist);
    }
    if (type === "entry") {
      updateChecklist(entryChecklist, setEntryChecklist);
    }
    if (type === "bed") {
      updateChecklist(bedChecklist, setBedChecklist);
    }
    if (type === "kitchen") {
      updateChecklist(kitchenChecklist, setKitchenChecklist);
    }
    if (type === "ensuite") {
      updateChecklist(ensuiteChecklist, setEnsuiteChecklist);
    }
    if (type === "drawers") {
      updateChecklist(drawersChecklist, setDrawersChecklist);
    }
    if (type === "others") {
      updateChecklist(othersChecklist, setOthersChecklist);
    }
  };

  const handleRadioChange = (index: number, value: string, type: string) => {
    if (isDisabled && completeStatus === "Complete") {
      console.log(
        "Radio change blocked: isDisabled=",
        isDisabled,
        "completeStatus=",
        completeStatus
      );
      return;
    }

    const updateChecklist = (
      checklist: Checklist[],
      setChecklist: React.Dispatch<React.SetStateAction<Checklist[]>>
    ) => {
      const updatedData = [...checklist];
      updatedData[index].status = value.toLowerCase();
      setChecklist(updatedData);
    };

    if (type === "external") {
      updateChecklist(externalChecklist, setExternalChecklist);
    }
    if (type === "entry") {
      updateChecklist(entryChecklist, setEntryChecklist);
    }
    if (type === "bed") {
      updateChecklist(bedChecklist, setBedChecklist);
    }
    if (type === "kitchen") {
      updateChecklist(kitchenChecklist, setKitchenChecklist);
    }
    if (type === "ensuite") {
      updateChecklist(ensuiteChecklist, setEnsuiteChecklist);
    }
    if (type === "drawers") {
      updateChecklist(drawersChecklist, setDrawersChecklist);
    }
    if (type === "others") {
      updateChecklist(othersChecklist, setOthersChecklist);
    }
  };

  const allChecklists = [
    ...externalChecklist,
    ...entryChecklist,
    ...bedChecklist,
    ...kitchenChecklist,
    ...ensuiteChecklist,
    ...drawersChecklist,
    ...othersChecklist,
  ];

  const allNotChecked =
    allChecklists.length > 0 &&
    allChecklists.every((item) => item.status?.toLowerCase() === "not checked");

  const checklistsWithSerialNumbers = [
    { data: externalChecklist, type: "external", subHeaderText: "EXTERNAL" },
    { data: entryChecklist, type: "entry", subHeaderText: "ENTRY" },
    { data: bedChecklist, type: "bed", subHeaderText: "BED" },
    { data: kitchenChecklist, type: "kitchen", subHeaderText: "KITCHEN" },
    { data: ensuiteChecklist, type: "ensuite", subHeaderText: "ENSUITE" },
    { data: drawersChecklist, type: "drawers", subHeaderText: "DRAWERS" },
    { data: othersChecklist, type: "others", subHeaderText: "OTHERS" },
  ];

  let serialNumberCounter = 0;

  return (
    <Box
      sx={{
        position: "relative",
        filter: loading ? "blur(5px)" : "none",
        pointerEvents: loading ? "none" : "auto",
      }}
    >
      <Typography
        sx={{
          mb: 2,
          fontWeight: "600",
          display: "flex",
          alignItems: "center",
          gap: "5px",
          color:
            completeStatus === "Incomplete" ? "error.main" : "success.main",
        }}
      >
        {completeStatus === "Incomplete" ? (
          <>⚠️ INCOMPLETE</>
        ) : (
          <>
            <OfflinePinIcon /> COMPLETE
          </>
        )}
      </Typography>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow className="table__head_row">
              <TableCell
                className="table__head_cell"
                sx={{
                  width: "1%",
                  textAlign: "center",
                  verticalAlign: "middle",
                  padding: "0px",
                }}
              ></TableCell>
              <TableCell
                className="table__head_cell"
                sx={{
                  width: "1%",
                  textAlign: "center",
                  verticalAlign: "middle",
                  padding: "0px",
                }}
              >
                S/N
              </TableCell>
              <TableCell className="table__head_cell" sx={{ width: "15%" }}>
                CHECKLIST
              </TableCell>
              <TableCell className="table__head_cell" sx={{ width: "15%" }}>
                NOTES
              </TableCell>
              <TableCell
                className="table__head_cell"
                sx={{ width: "5%" }}
                align="center"
              >
                STATUS
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Box
                    sx={{
                      position: "absolute",
                      top: "60%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      zIndex: 10,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : apiError ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="error">Invalid DC Number</Typography>
                </TableCell>
              </TableRow>
            ) : (
              <React.Fragment>
                {checklistsWithSerialNumbers.map((checklistSection) => {
                  const serialNumberStart = serialNumberCounter + 1;
                  serialNumberCounter += checklistSection.data.length;

                  console.log(
                    `Rendering ${checklistSection.type} with serialNumberStart: ${serialNumberStart}, item count: ${checklistSection.data.length}`
                  );

                  return (
                    <MultiSubheaderTableRowComponent
                      key={checklistSection.type}
                      subHeaderText={checklistSection.subHeaderText}
                      type={checklistSection.type}
                      data={checklistSection.data}
                      handleNotesChange={handleNotesChange}
                      handleRadioChange={handleRadioChange}
                      disabled={isDisabled && completeStatus === "Complete"}
                      allNotChecked={allNotChecked}
                      serialNumberStart={serialNumberStart}
                      chassisDCNumber={"DC" + chassisNo}
                      deptName={"qa-quality-inspection"}
                      activeCheckpointList={activeCheckpointList}
                      checkCheckpointRecord={checkCheckpointRecord}
                    />
                  );
                })}
              </React.Fragment>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Grid container spacing={3} my={4}>
        <Grid item md={4}>
          <TextField
            label="Checked By"
            value={checkedBy}
            onChange={(e) => {
              if (isDisabled && completeStatus === "Complete") {
                console.log(
                  "Checked By change blocked: isDisabled=",
                  isDisabled,
                  "completeStatus=",
                  completeStatus
                );
                return;
              }
              setLocalCheckedBy(e.target.value);
              setCheckedBy(e.target.value);
            }}
            fullWidth
            hidden={apiError}
            disabled={isDisabled && completeStatus === "Complete"}
          />
        </Grid>
        <Grid item md={4}>
          <TextField
            label="Date"
            type="date"
            value={date}
            onChange={(e) => {
              if (isDisabled && completeStatus === "Complete") {
                console.log(
                  "Date change blocked: isDisabled=",
                  isDisabled,
                  "completeStatus=",
                  completeStatus
                );
                return;
              }
              setDate(e.target.value);
            }}
            fullWidth
            InputLabelProps={{ shrink: true }}
            hidden={apiError}
            disabled={isDisabled && completeStatus === "Complete"}
          />
        </Grid>
      </Grid>

      <Button
        variant="contained"
        color="primary"
        onClick={saveData}
        disabled={
          allChecklists.length === 0 ||
          apiError ||
          loading ||
          (!chassisNo && !model) ||
          completeStatus === "Complete"
        }
      >
        Save
      </Button>
    </Box>
  );
};

export default QualityInspectionChecklist;
